import React from 'react'
import { Link } from 'react-router-dom'
import { 
  Rocket, 
  Play, 
  MousePointer, 
  Users, 
  Zap,
  BarChart3,
  PenTool,
  Database,
  Check,
  ArrowRight
} from 'lucide-react'
import Header from '../components/layout/Header'

const LandingPage: React.FC = () => {
  const features = [
    {
      icon: MousePointer,
      title: 'Drag-and-Drop Canvas',
      description: 'Build workflows visually with nodes for agents, tasks, and tools.',
      color: 'blue'
    },
    {
      icon: Zap,
      title: 'No-Code Simplicity',
      description: 'Focus on what your agents should do, not how to wire Python scripts.',
      color: 'green'
    },
    {
      icon: Database,
      title: 'Transparent Pricing',
      description: 'You only pay for what you run — more workflows or more agents = more credits, nothing else.',
      color: 'purple'
    }
  ]

  const crewAIFeatures = [
    {
      title: 'Customizable Agents',
      description: 'Define roles, goals, and tools for each agent.'
    },
    {
      title: 'Task Coordination',
      description: 'Seamless task delegation and collaboration between agents.'
    },
    {
      title: 'Simple Execution',
      description: 'Kick off your crew with a single command and get results fast.'
    }
  ]

  const howItWorksSteps = [
    {
      step: 1,
      title: 'Create Your Crew',
      description: 'Add agents with specific roles and goals.'
    },
    {
      step: 2,
      title: 'Define Tasks',
      description: 'Assign responsibilities and connect them into a flow.'
    },
    {
      step: 3,
      title: 'Kickoff',
      description: 'Launch your crew and watch it run in real-time with live progress updates.'
    }
  ]

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto text-center">
          <div className="mb-8">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Design and Run Agentic Workflows{' '}
              <span style={{color: 'var(--color-primary)'}}>
                Visually
              </span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed">
              Build powerful multi-agent workflows without writing a single line of code. flowran lets you design, connect, and launch AI-powered crews in minutes — Let it Flow.
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Link
              to="/dashboard"
              className="text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors hover:opacity-90 flex items-center justify-center space-x-3"
              style={{backgroundColor: 'var(--color-primary)'}}
            >
              <Rocket className="w-5 h-5" />
              <span>Let it Flow</span>
            </Link>
            <button className="border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800 px-8 py-4 rounded-lg text-lg font-semibold transition-colors flex items-center justify-center space-x-3">
              <Play className="w-5 h-5" />
              <span>Learn More About CrewAI</span>
            </button>
          </div>
          
          {/* Hero Visual - Realistic Dashboard Canvas */}
          <div className="relative max-w-5xl mx-auto">
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
              <div className="relative h-[400px] bg-white rounded-lg overflow-hidden" style={{
                backgroundImage: `
                  radial-gradient(circle at 1px 1px, rgba(0,0,0,0.05) 1px, transparent 0)
                `,
                backgroundSize: '20px 20px'
              }}>
                
                {/* Straight Connection Lines */}
                <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 1 }}>
                  {/* Start to Research Agent */}
                  <line 
                    x1="120" y1="200" x2="200" y2="200"
                    stroke="#9CA3AF" 
                    strokeWidth="2"
                  />
                  {/* Research to Data Analyst */}
                  <line 
                    x1="392" y1="200" x2="472" y2="200"
                    stroke="#9CA3AF" 
                    strokeWidth="2"
                  />
                  {/* Data Analyst to Writer */}
                  <line 
                    x1="664" y1="200" x2="744" y2="200"
                    stroke="#9CA3AF" 
                    strokeWidth="2"
                  />
                </svg>
                
                {/* Real Start Node */}
                <div 
                  className="absolute w-16 h-16 text-white rounded-full flex items-center justify-center shadow-lg"
                  style={{
                    top: '168px',
                    left: '40px',
                    backgroundColor: 'var(--color-primary)',
                    zIndex: 2
                  }}
                >
                  <img 
                    src="/icons/start-button.png" 
                    alt="Start" 
                    className="h-8 object-contain"
                    style={{
                      filter: 'brightness(0) invert(1)',
                      imageRendering: 'crisp-edges',
                      maxWidth: '48px'
                    }}
                  />
                </div>
                
                {/* Research Agent Node */}
                <div className="absolute bg-white rounded-lg shadow-lg border border-gray-200 p-3" 
                     style={{ top: '160px', left: '200px', width: '192px', height: '80px', zIndex: 2 }}>
                  <div className="flex items-center h-full">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center mr-3" 
                         style={{backgroundColor: 'var(--color-node-agent-bg)'}}>
                      <Users className="w-5 h-5" style={{color: 'var(--color-node-agent)'}} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-gray-900 text-sm">Research Agent</div>
                      <div className="text-xs text-gray-500">Market researcher</div>
                    </div>
                  </div>
                </div>
                
                {/* Data Analyst Node (middle) */}
                <div className="absolute bg-white rounded-lg shadow-lg border border-gray-200 p-3" 
                     style={{ top: '160px', left: '472px', width: '192px', height: '80px', zIndex: 2 }}>
                  <div className="flex items-center h-full">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center mr-3" 
                         style={{backgroundColor: 'var(--color-node-agent-bg)'}}>
                      <BarChart3 className="w-5 h-5" style={{color: 'var(--color-node-agent)'}} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-gray-900 text-sm">Data Analyst</div>
                      <div className="text-xs text-gray-500">Process insights</div>
                    </div>
                  </div>
                </div>
                
                {/* Writer Agent Node */}
                <div className="absolute bg-white rounded-lg shadow-lg border border-gray-200 p-3" 
                     style={{ top: '160px', left: '744px', width: '192px', height: '80px', zIndex: 2 }}>
                  <div className="flex items-center h-full">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center mr-3" 
                         style={{backgroundColor: 'var(--color-node-task-bg)'}}>
                      <PenTool className="w-5 h-5" style={{color: 'var(--color-node-task)'}} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-gray-900 text-sm">Content Writer</div>
                      <div className="text-xs text-gray-500">Blog post creator</div>
                    </div>
                  </div>
                </div>
                
                
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Why We Built flowran
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed">
            Agentic workflows are the future of automation — but today, building them is either too complex for non-programmers, or too expensive for teams to experiment freely. Existing platforms lock you into steep monthly fees, forcing you to overpay whether you run one workflow or one thousand.
          </p>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              A Visual Builder for Everyone
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-12">
              flowran solves this by giving you:
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon
              return (
                <div key={index} className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300">
                  <div className={`w-16 h-16 bg-${feature.color}-100 dark:bg-${feature.color}-900/20 rounded-2xl flex items-center justify-center mb-6`}>
                    <Icon className={`text-${feature.color}-600 dark:text-${feature.color}-400 w-8 h-8`} />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Powered by CrewAI Section */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">
              Powered by CrewAI
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-4xl mx-auto mb-12">
              flowran is built on top of CrewAI, an open-source multi-agent framework designed to orchestrate autonomous AI agents. By combining CrewAI's power with a visual interface, flowran makes multi-agent systems accessible to anyone — from developers to product managers to business teams.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {crewAIFeatures.map((feature, index) => (
              <div key={index} className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 px-6 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              How It Works
            </h2>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {howItWorksSteps.map((item, index) => (
              <div key={index} className="text-center">
                <div 
                  className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
                  style={{backgroundColor: 'var(--color-primary)'}}
                >
                  <span className="text-2xl font-bold text-white">{item.step}</span>
                </div>
                <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                  {item.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">
            Fair, Usage-Based Pricing
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 mb-8">
            No subscriptions. No paywalls.
          </p>
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg border border-gray-200 dark:border-gray-700 max-w-2xl mx-auto">
            <p className="text-lg text-gray-700 dark:text-gray-300 mb-6">
              You pay for what you run — meaning:
            </p>
            <div className="space-y-4 text-left">
              {[
                'Small workflows cost pennies.',
                'Large, multi-agent workflows cost proportionally more.',
                'You stay in control of your spend while experimenting and scaling.'
              ].map((item, index) => (
                <div key={index} className="flex items-center">
                  <Check className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 px-6 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">
            Get creative with your AI crew — for free.
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 mb-8">
            Sign up, build your first workflow, and only pay when you kick it off.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/dashboard"
              className="text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors hover:opacity-90 flex items-center justify-center space-x-3"
              style={{backgroundColor: 'var(--color-primary)'}}
            >
              <Rocket className="w-5 h-5" />
              <span>Let it Flow</span>
            </Link>
            <button className="border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800 px-8 py-4 rounded-lg text-lg font-semibold transition-colors flex items-center justify-center space-x-3">
              <ArrowRight className="w-5 h-5" />
              <span>Learn More About CrewAI</span>
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 py-12 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Brand */}
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center mb-4">
                <img 
                  src="/images/logos/flowran-m.png" 
                  alt="flowran" 
                  className="h-16"
                />
              </div>
              <p className="text-gray-600 dark:text-gray-300 mb-6 max-w-md">
                The visual builder for CrewAI workflows. Transform complex multi-agent systems into intuitive drag-and-drop experiences.
              </p>
            </div>
            
            {/* Product */}
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-4">Product</h4>
              <ul className="space-y-2 text-gray-600 dark:text-gray-300">
                <li><span className="hover:text-gray-900 dark:hover:text-white cursor-pointer">Features</span></li>
                <li><span className="hover:text-gray-900 dark:hover:text-white cursor-pointer">Pricing</span></li>
                <li><span className="hover:text-gray-900 dark:hover:text-white cursor-pointer">Templates</span></li>
                <li><span className="hover:text-gray-900 dark:hover:text-white cursor-pointer">Integrations</span></li>
              </ul>
            </div>
            
            {/* Support */}
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-4">Support</h4>
              <ul className="space-y-2 text-gray-600 dark:text-gray-300">
                <li><span className="hover:text-gray-900 dark:hover:text-white cursor-pointer">Documentation</span></li>
                <li><span className="hover:text-gray-900 dark:hover:text-white cursor-pointer">API Reference</span></li>
                <li><span className="hover:text-gray-900 dark:hover:text-white cursor-pointer">Community</span></li>
                <li><span className="hover:text-gray-900 dark:hover:text-white cursor-pointer">Contact</span></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-200 dark:border-gray-800 mt-12 pt-8 flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
            <p>© 2024 flowran. All rights reserved.</p>
            <div className="flex items-center space-x-6">
              <span className="hover:text-gray-900 dark:hover:text-white cursor-pointer">Privacy Policy</span>
              <span className="hover:text-gray-900 dark:hover:text-white cursor-pointer">Terms of Service</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default LandingPage
